import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getExtraChargesByProjectId,
  addExtraCharge,
  updateExtraCharge,
} from "../../services/extraChargeService";

const ExtraChargeManager = () => {
  const { id: projectId } = useParams();

  const [charges, setCharges] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [approvalType, setApprovalType] = useState("manual");
  const [isPaid, setIsPaid] = useState(0);

  const [error, setError] = useState("");

  /* ================= FETCH ================= */

  const fetchCharges = useCallback(async () => {
    try {
      const data = await getExtraChargesByProjectId(projectId);
      setCharges(data);
    } catch {
      setError("Failed to load extra charges.");
    }
  }, [projectId]);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  /* ================= HANDLERS ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      title,
      projectId,
      amount: parseFloat(amount),
      note,
      dueDate,
      approvalType,
      isPaid: parseInt(isPaid),
    };

    try {
      if (editingItem) {
        await updateExtraCharge({ ...payload, id: editingItem.id });
      } else {
        await addExtraCharge(payload);
      }

      setTitle("");
      setAmount("");
      setNote("");
      setDueDate("");
      setApprovalType("manual");
      setIsPaid(0);
      setEditingItem(null);

      fetchCharges();
    } catch {
      setError("Failed to save extra charge.");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title);
    setAmount(item.amount);
    setNote(item.note);
    setDueDate(item.dueDate);
    setApprovalType(item.approvalType);
    setIsPaid(item.isPaid ?? 0);
  };

  /* ================= UI ================= */

  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Extra Charges</h1>
          <p className="text-sm text-gray-500">
            Manage additional costs for this project
          </p>
        </div>

        <Link
          to={`/projects/${projectId}`}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back to Project
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {/* FORM */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8 max-w-3xl">
        <h2 className="text-lg font-semibold mb-4">
          {editingItem ? "Edit Extra Charge" : "Add Extra Charge"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full border rounded px-3 py-2"
          />

          <input
            type="number"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="w-full border rounded px-3 py-2"
          />

          <textarea
            placeholder="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            rows="3"
            className="w-full border rounded px-3 py-2"
          />

          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />

          <select
            value={approvalType}
            onChange={(e) => setApprovalType(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="client">Client Approval Required</option>
            <option value="manual">Already Paid / Internal Expense</option>
          </select>

          <select
            value={isPaid}
            onChange={(e) => setIsPaid(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value={0}>Unpaid</option>
            <option value={1}>Paid</option>
          </select>

          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {editingItem ? "Update" : "Add"} Charge
          </button>
        </form>
      </div>

      {/* ================= MOBILE VIEW (CARDS) ================= */}
      <div className="md:hidden space-y-4">
        {charges.length > 0 ? (
          charges.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-xl shadow-sm p-4 border"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold">{c.title}</h3>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    c.isPaid
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {c.isPaid ? "Paid" : "Unpaid"}
                </span>
              </div>

              <p className="text-sm text-gray-600 mt-1">
                ₹ {c.amount}
              </p>

              <p className="text-sm text-gray-500 mt-2">{c.note}</p>

              <p className="text-xs text-gray-400 mt-2">
                Added on: {c.addedOn || "-"}
              </p>

              <button
                onClick={() => handleEdit(c)}
                className="mt-3 text-blue-600 text-sm hover:underline"
              >
                ✏️ Edit
              </button>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500">
            No extra charges added yet.
          </p>
        )}
      </div>

      {/* ================= DESKTOP VIEW (TABLE) ================= */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Amount</th>
              <th className="p-3 text-left">Note</th>
              <th className="p-3 text-left">Payment</th>
              <th className="p-3 text-left">Added On</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {charges.length > 0 ? (
              charges.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="p-3">{c.title}</td>
                  <td className="p-3">{c.amount}</td>
                  <td className="p-3">{c.note}</td>
                  <td className="p-3">
                    {c.isPaid ? (
                      <span className="text-green-600 font-semibold">
                        Paid
                      </span>
                    ) : (
                      <span className="text-red-600 font-semibold">
                        Unpaid
                      </span>
                    )}
                  </td>
                  <td className="p-3">{c.addedOn || "-"}</td>
                  <td className="p-3">
                    <button
                      onClick={() => handleEdit(c)}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="p-6 text-center text-gray-500"
                >
                  No extra charges added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ExtraChargeManager;
